<template>
    <div class="article-box-wrapper">
        <router-link :to="articleUrl" class="article-card-link">
            <div class="article-image-wrapper" v-lazyload :style="{backgroundImage: `url('${article.imgUrl}')`}">
                <!-- Desktop: Title overlay on image -->
                <div class="article-overlay-desktop">
                    <div class="article-content-overlay">
                        <h3 class="article-title-overlay">{{article.title}}</h3>
                    </div>
                </div>
            </div>
            <div class="article-card-footer">
                <!-- Mobile: Title and date below image -->
                <div class="article-footer-content">
                    <h3 class="article-footer-title">{{article.title}}</h3>
                    <div class="article-footer-date" v-if="!isGiftList && formattedDate">{{formattedDate}}</div>
                </div>
                <!-- Desktop: Date and button -->
                <div class="article-footer-meta-desktop">
                    <div class="article-footer-date-desktop" v-if="!isGiftList && formattedDate">{{formattedDate}}</div>
                    <div class="article-footer-button">
                        <span class="button-text">Explore</span>
                        <span class="material-symbols-outlined button-icon">arrow_forward</span>
                    </div>
                </div>
            </div>
        </router-link>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue'
    import { useRoute } from 'vue-router'
    import { Article } from '@/classes/Articles'

    const props = defineProps<{
        article: Article
    }>()

    const route = useRoute()
    const articleUrl = ref('')
    const isGiftList = ref(false)

    const formattedDate = computed(() => {
        if (!props.article.date) return ''
        const date = new Date(props.article.date)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    })

    const init = () => {
        if (route.name === 'giftideas' || props.article.isGiftList) {
            isGiftList.value = true
            articleUrl.value = '/gift-ideas/' + props.article.articleId + '/' + props.article.prettyTitle
        } else {
            isGiftList.value = false
            articleUrl.value = '/list/' + props.article.articleId + '/' + props.article.prettyTitle
        }
    }

    init()
</script>

<style scoped>
.article-box-wrapper {
    width: 100%;
    margin-bottom: 24px;
    box-sizing: border-box;
    min-width: 0;
}

.article-card-link {
    display: block;
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-decoration: none;
    color: inherit;
    height: 100%;
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;
    min-width: 0;
}

.article-card-link:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.article-image-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 21 / 9;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    overflow: hidden;
    flex-shrink: 0;
    min-width: 0;
    height: auto;
}

/* Desktop: Title overlay on image */
.article-overlay-desktop {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.5) 40%, transparent 100%);
    padding: 32px 28px 28px;
    transition: background 0.3s ease;
}

.article-card-link:hover .article-overlay-desktop {
    background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 40%, transparent 100%);
}

.article-content-overlay {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.article-title-overlay {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    margin: 0;
    line-height: 1.3;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.article-card-footer {
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
}

.article-footer-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.article-footer-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.article-footer-date {
    font-size: 0.875rem;
    color: #666;
    font-weight: 400;
}

/* Desktop: Footer meta (date + button) */
.article-footer-meta-desktop {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
}

.article-footer-date-desktop {
    font-size: 0.875rem;
    color: #666;
    font-weight: 400;
}

.article-footer-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #FF8087;
    font-weight: 600;
    font-size: 0.9375rem;
    transition: gap 0.2s ease;
}

.article-card-link:hover .article-footer-button {
    gap: 12px;
}

.button-text {
    transition: transform 0.2s ease;
}

.article-card-link:hover .button-text {
    transform: translateX(2px);
}

.button-icon {
    font-size: 20px;
    transition: transform 0.2s ease;
}

.article-card-link:hover .button-icon {
    transform: translateX(4px);
}

/* Desktop: Show overlay, show footer meta */
@media (min-width: 1201px) {
    .article-overlay-desktop {
        display: block;
    }

    .article-footer-title {
        display: none; /* Hide title in footer on desktop since it's in overlay */
    }

    .article-footer-date {
        display: none; /* Hide date in footer on desktop, use desktop version */
    }

    .article-footer-meta-desktop {
        display: flex;
    }

    .article-card-footer {
        padding: 24px 28px;
    }
}

/* Mobile: Hide overlay, show title below image */
@media (max-width: 1200px) {
    .article-card-link {
        border-radius: 8px;
    }

    .article-image-wrapper {
        aspect-ratio: 21 / 9; /* 21:9 banner orientation for mobile */
        height: 0;
        padding-bottom: 42.857%; /* 9/21 * 100 = 42.857% fallback for 21:9 */
        min-height: 0;
        background-size: cover;
        background-position: center;
    }

    .article-overlay-desktop {
        display: none;
    }

    .article-footer-title {
        display: -webkit-box; /* Show title in footer on mobile */
    }

    .article-footer-date {
        display: block; /* Show date in footer on mobile */
    }

    .article-footer-meta-desktop {
        display: none; /* Hide desktop meta on mobile */
    }

    .article-card-footer {
        padding: 16px;
        gap: 12px;
    }

    .article-footer-button {
        display: none; /* Hide button on mobile for cleaner look */
    }
}

/* Small mobile */
@media (max-width: 640px) {
    .article-box-wrapper {
        margin-bottom: 20px;
    }

    .article-image-wrapper {
        aspect-ratio: 21 / 9; /* 21:9 banner orientation for mobile */
        height: 0;
        padding-bottom: 42.857%; /* 9/21 * 100 = 42.857% fallback for 21:9 */
        min-height: 0;
    }

    .article-card-footer {
        padding: 14px 12px;
        gap: 10px;
    }

    .article-footer-title {
        font-size: 1rem;
        line-height: 1.3;
    }

    .article-footer-date {
        font-size: 0.8125rem;
    }
}
</style>
